import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import particlesConfig from '../../Shared/pr-s-black';
import '../../Assets/Stylesheets/Pages/Blog/Blog.scss';
import { API_BASE_URL } from '../../config/api';

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  useEffect(() => {
    fetchBlogPost();
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/blog/posts/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Blog post not found');
        }
        throw new Error('Failed to fetch blog post');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setPost(data.post);
      } else {
        setError(data.error || 'Failed to load blog post');
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError(err.message || 'Unable to load blog post. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="blog-page">
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesConfig}
          className="particles-background"
        />
        <div className="blog-content">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-page">
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesConfig}
          className="particles-background"
        />
        <div className="blog-content">
          <h1>Error</h1>
          <p className="error-message">{error || 'Blog post not found'}</p>
          <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page blog-detail-page">
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={particlesConfig}
        className="particles-background"
      />
      <div className="blog-content">
        <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
        
        <article className="blog-article">
          <header className="blog-article-header">
            <h1>{post.title}</h1>
            <div className="blog-meta">
              <span className="blog-author">By {post.author}</span>
              <span className="blog-date">{formatDate(post.published_at)}</span>
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="blog-tags">
                {post.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </header>

          {post.featured_image && (
            <div className="blog-featured-image">
              <img src={post.featured_image} alt={post.title} />
            </div>
          )}

          <div className="blog-article-content">
            {post.content.split('\n').map((paragraph, index) => {
              // Check if it's a heading
              if (paragraph.startsWith('## ')) {
                return <h2 key={index}>{paragraph.replace('## ', '')}</h2>;
              } else if (paragraph.startsWith('### ')) {
                return <h3 key={index}>{paragraph.replace('### ', '')}</h3>;
              } else if (paragraph.trim() === '') {
                return null;
              }
              return <p key={index}>{paragraph}</p>;
            })}
          </div>
        </article>
      </div>
    </div>
  );
}

